import { createSlice } from '@reduxjs/toolkit';
import {
    composeSMSThunk,
    deleteSMSThunk,
    fetchSMSMessagesThunk,
    fetchUserSMSIntegrationThunk,
    fetchUserSMSThunk,
    markAllSMSAsReadThunk,
    replyToSMSThreadThunk,
    sendBulkSMSThunk
} from '../thunks/sms';
import { SMSObject, SMSState, SMSThreadObject } from '../../params/redux/sms';

// Initial state with default values for authentication and user info
const initialState: SMSState = {
    sms: [],
    selectedSms: null,
    smsThread: [],
    flags: {
        isIntegrated: false,
        composing: false
    },
    calls: {
        device: null,
        status: '',
        isCalling: false
    },
    loaders: {
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isSearching: false,
        isInserting: false
    }
};

// Slice for app-related states and actions
const smsSlice = createSlice({
    // Slice name
    name: 'sms',
    // inital state for the slice
    initialState,
    reducers: {
        settingCallingStatus: (state, action) => {
            const { device, status, isCalling } = action.payload;
            state.calls.device = device;
            state.calls.status = status;
            state.calls.isCalling = isCalling;
        },
        settingSelectedSMS: (state, action) => {
            state.selectedSms = action.payload;
        },
        settingComposingFlagSMS: (state, action) => {
            state.flags.composing = action.payload;
        },
        // Resets loading states related to app actions
        resettingSMSLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingSMSData: () => initialState
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(fetchUserSMSIntegrationThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchUserSMSIntegrationThunk.fulfilled, (state, action) => {
                const data = action.payload as string[];
                const isExisting = data.find((item) => item === 'twilio');
                if (isExisting) {
                    state.flags.isIntegrated = true;
                } else {
                    state.flags.isIntegrated = false;
                }
            })
            .addCase(fetchUserSMSIntegrationThunk.rejected, (state) => {
                state.flags.isIntegrated = false;
            });
        builder
            .addCase(fetchUserSMSThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchUserSMSThunk.fulfilled, (state, action) => {
                state.sms = action.payload as SMSObject[];
            })
            .addCase(fetchUserSMSThunk.rejected, (state) => {
                state.sms = [];
            });
        builder
            .addCase(composeSMSThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(composeSMSThunk.fulfilled, () => {
                //
            })
            .addCase(composeSMSThunk.rejected, () => {
                //
            });
        builder
            .addCase(replyToSMSThreadThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(replyToSMSThreadThunk.fulfilled, () => {
                //
            })
            .addCase(replyToSMSThreadThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchSMSMessagesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchSMSMessagesThunk.fulfilled, (state, action) => {
                state.smsThread = action.payload as SMSThreadObject[];
            })
            .addCase(fetchSMSMessagesThunk.rejected, (state) => {
                state.smsThread = [];
            });
        builder
            .addCase(markAllSMSAsReadThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(markAllSMSAsReadThunk.fulfilled, () => {
                //
            })
            .addCase(markAllSMSAsReadThunk.rejected, () => {
                //
            });
        builder
            .addCase(deleteSMSThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isDeleting = true;
            })
            .addCase(deleteSMSThunk.fulfilled, () => {
                //
            })
            .addCase(deleteSMSThunk.rejected, () => {
                //
            });
        builder
            .addCase(sendBulkSMSThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(sendBulkSMSThunk.fulfilled, () => {
                //
            })
            .addCase(sendBulkSMSThunk.rejected, () => {
                //
            });
    }
});

// Exporting actions for updating app state
export const {
    settingCallingStatus,
    settingComposingFlagSMS,
    settingSelectedSMS,
    resettingSMSLoader,
    resettingSMSData
} = smsSlice.actions;

// Exporting the reducer to be used in the store
export default smsSlice.reducer;
